// pdf.service.ts
import { Injectable } from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { BinaryFontsService } from './binary-fonts.service';
import { setUpPdfMake, createDocDefinition } from './pdf-config';
import { createSectionContent, createColumn, getSectionWidth } from './pdf-content.builder';
import { PdfSidebarBuilder } from './pdf-sidebar.builder';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root'
})
export class PdfService {
  dataSource: any;
  statisticTable: any[] = [];
  currentLanguage!: string;
  charts: any[] = [];
  comment: string = '';
  headerContent: any;
  productData: any;
  private pdfSidebarBuilder: PdfSidebarBuilder = new PdfSidebarBuilder();

  constructor(private _binaryFontsService: BinaryFontsService) {}

  public reset(): void {
    this.dataSource = null;
    this.statisticTable = [];
    //this.currentLanguage = '';
    this.charts = [];
    this.comment = '';
    //this.headerContent = []
    this.pdfSidebarBuilder.sidebarActive = false;
  }

  // NEUE Methode zum Initialisieren des Headers:
  public initializeHeader(productTitle: string, reportTitle: string, reportDate: string): void {
    this.headerContent = {
      title: `${productTitle}\n${reportTitle} – ${reportDate}`,
      name: productTitle
    };
  }

  public initializeProduct(product: any): void {
    this.productData = product;
  }


  initializeComment(comment: string): void {
    this.comment = comment;
  }

  initializeDataSource(data: any) {
    this.dataSource = data;
  }

  initializeCurrentLanguage(language: string) {
    this.currentLanguage = language;
  }

  initializeStatisticTable(data: any) {
    /*
    const index = this.statisticTable.findIndex(item => item.id === data.id);
    if (index !== -1) {
      this.statisticTable[index] = data;
    } else {
      this.statisticTable.push(data);
    }

     */
    this.statisticTable.push(data);
  }

  appendChart(chartImage: any) {
    this.charts.push(chartImage);
  }

  /**
   * Diese Methode baut das docDefinition-Objekt zusammen, basierend auf den
   * aktuellen Daten, Charts und Sidebar-Einstellungen.
   */
  public buildDocDefinition(): any {
    const content: any[] = [];
    let columns: any[] = [];
    let currentWidth = 0;
    // Flag, das anzeigt, ob wir auf einer neuen Seite sind.
    let newPage = true;

    setUpPdfMake(this._binaryFontsService);

    for (const section of this.dataSource) {
      const translation = section.translations[this.currentLanguage];
      if (!translation) continue;

      console.log(section);

      // Falls es sich um eine Sidebar handelt:
      if (translation.title.toLowerCase() === 'sidebar') {
        // Falls bereits Spalteninhalt vorhanden ist, flushen wir diese und erzwingen einen Seitenumbruch.
        if (columns.length > 0) {
          content.push({ columns: columns, columnGap: 12 });
          content.push({ text: '', pageBreak: 'before' });
          columns = [];
          currentWidth = 0;
          newPage = true; // Wir beginnen eine neue Seite.
        } else if (!newPage && this.pdfSidebarBuilder.sidebarActive) {
          // Falls wir nicht auf einer neuen Seite sind und bereits Sidebar aktiv ist,
          // erzwingen wir einen Seitenumbruch.
          content.push({ text: '', pageBreak: 'before' });
          newPage = true;
        }

        // Wenn wir gerade auf einer neuen Seite sind, fügen wir den Header ein.
        if (newPage && this.headerContent && this.headerContent.length > 0) {
          content.push(...this.headerContent);
          console.log(this.headerContent);
          // Optional: Du kannst hier KEINEN zusätzlichen Seitenumbruch erzwingen,
          // wenn der Header und der nachfolgende Sidebar-Inhalt auf derselben Seite erscheinen sollen.
          newPage = false; // Header wurde eingefügt – nicht mehr "neu".
        }

        // Sidebar-Inhalt einfügen
        const sidebarElements = this.pdfSidebarBuilder.processSidebarSection(section, this.currentLanguage);
        content.push(...sidebarElements);
        this.pdfSidebarBuilder.sidebarActive = true;
        // Wir gehen davon aus, dass der nächste Sidebar-Abschnitt (falls vorhanden)
        // auf derselben Seite fortgesetzt wird – sofern kein manueller Seitenumbruch folgt.
        continue;
      }

      // Bei explizitem Seitenumbruch:
      if (translation.title.toLowerCase() === 'pagebreak') {
        content.push({ text: '', pageBreak: 'before' });
        this.pdfSidebarBuilder.sidebarActive = false;
        newPage = true; // Nach einem Seitenumbruch beginnt eine neue Seite.
        continue;
      }

      // Normale Sektion verarbeiten:
      const sectionContent = createSectionContent(
        section,
        this.currentLanguage,
        this.statisticTable,
        this.charts,
        this.pdfSidebarBuilder.sidebarActive,
        this.comment,
        this.productData
      );
      let wrappedContent: any = sectionContent;
      if (this.pdfSidebarBuilder.sidebarActive) {
        wrappedContent = {
          margin: [130, 0, 0, 0],
          stack: sectionContent
        };
      }
      const column = createColumn(section, [wrappedContent]);
      const sectionWidth = getSectionWidth(section.width);
      const maxWidth = this.pdfSidebarBuilder.sidebarActive ? 70 : 100;
      if (currentWidth + sectionWidth > maxWidth) {
        content.push({ columns: columns, columnGap: 12 });
        columns = [];
        currentWidth = 0;
        newPage = true; // Wir erzwingen hier einen Seitenumbruch.
      }
      columns.push(column);
      currentWidth += sectionWidth;
      // Für normale (keine Sidebar) Sektionen gehen wir davon aus, dass wir NICHT automatisch auf einer neuen Seite starten.
      newPage = false;
    }

    if (columns.length > 0) {
      content.push({ columns: columns, columnGap: 12 });
    }

    // Aktualisiere sidebarActive als abschließende Information:
    this.pdfSidebarBuilder.sidebarActive = this.dataSource.some((section: any) => {
      const translation = section.translations[this.currentLanguage];
      return translation && translation.title.toLowerCase() === 'sidebar';
    });

    const logoSettings = this.pdfSidebarBuilder.sidebarActive
      ? { x: 10, y: 20, width: 40, svg: this._binaryFontsService.icon.icon }
      : { x: 40, y: 40, width: 140, svg: this._binaryFontsService.logo.logo };

    const docDefinition = createDocDefinition(content, logoSettings, this.pdfSidebarBuilder.sidebarActive, this.headerContent, this._binaryFontsService.logoOptiroi.logo);
    return docDefinition;
  }




  /**
   * Diese Methode erstellt ein PDF (mittels pdfMake) und liefert den Base64-String.
   */
  public generatePdfBinary(docDefinition: any): Promise<string> {
    return new Promise((resolve, reject) => {
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      pdfDocGenerator.getBase64((data: string) => {
        resolve(data);
      });
    });
  }

  generatePdf() {
    const content: any[] = [];
    let columns: any[] = [];
    let currentWidth = 0;

    // pdfMake konfigurieren
    setUpPdfMake(this._binaryFontsService);

    // Alle Sektionen durchlaufen
    this.dataSource.forEach((section: any) => {
      const translation = section.translations[this.currentLanguage];
      if (!translation) {
        return;
      }

      // Sidebar-Sektion
      if (translation.title.toLowerCase() === 'sidebar') {
        // Falls bereits normaler Content (in "columns") vorhanden ist, diesen zuerst flushen und einen manuellen PageBreak erzwingen:
        if (columns.length > 0) {
          content.push({ columns: columns, columnGap: 12 });
          // Erzeuge einen manuellen Seitenumbruch
          content.push({ text: '', pageBreak: 'before' });
          columns = [];
          currentWidth = 0;
        }
          // Falls schon eine Sidebar aktiv ist (d.h. wir befinden uns nicht auf der allerersten Seite mit Sidebar),
        // erzwinge ebenfalls einen Seitenumbruch, bevor die neue Sidebar verarbeitet wird.
        else if (this.pdfSidebarBuilder.sidebarActive) {
          content.push({ text: '', pageBreak: 'before' });
        }
        const sidebarElements = this.pdfSidebarBuilder.processSidebarSection(section, this.currentLanguage);
        content.push(...sidebarElements);
        this.pdfSidebarBuilder.sidebarActive = true;
        return;
      }


      // Expliziter Seitenumbruch
      if (translation.title.toLowerCase() === 'pagebreak') {
        content.push({ text: '', pageBreak: 'before' });
        this.pdfSidebarBuilder.sidebarActive = false;
        return;
      }

      // Normale Sektion
      const sectionContent = createSectionContent(section, this.currentLanguage, this.statisticTable, this.charts, this.pdfSidebarBuilder.sidebarActive, this.comment);
      let wrappedContent: any = sectionContent;
      if (this.pdfSidebarBuilder.sidebarActive) {
        wrappedContent = {
          margin: [130, 0, 0, 0],
          stack: sectionContent
        };
      }

      const column = createColumn(section, [wrappedContent]);
      const sectionWidth = getSectionWidth(section.width);

      // Mit:
      const maxWidth = this.pdfSidebarBuilder.sidebarActive ? 70 : 100;
      if (currentWidth + sectionWidth > maxWidth) {
        content.push({ columns: columns, columnGap: 12 });
        columns = [];
        currentWidth = 0;
      }

      columns.push(column);
      currentWidth += sectionWidth;
    });

    if (columns.length > 0) {
      content.push({ columns: columns, columnGap: 12 });
    }

    // Vor dem Logo setzen: Aktualisiere den Flag, wenn eine Sidebar existiert.
    this.pdfSidebarBuilder.sidebarActive = this.dataSource.some((section: any) => {
      const translation = section.translations[this.currentLanguage];
      return translation && translation.title.toLowerCase() === 'sidebar';
    });

    const logoSettings = this.pdfSidebarBuilder.sidebarActive
      ? { x: 10, y: 20, width: 40, svg: this._binaryFontsService.icon.icon }
      : { x: 40, y: 40, width: 140, svg: this._binaryFontsService.logo.logo };


    const docDefinition = createDocDefinition(content, logoSettings, this.pdfSidebarBuilder.sidebarActive, this.headerContent, this._binaryFontsService.logoOptiroi.logo);
    pdfMake.createPdf(docDefinition).open();
  }
}
